import React, { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { Popover as MuiPopover } from '@mui/material';
import classNames from 'classnames';
import css from './MetricFilter.css';
import Chevron from '../../../../resources/icons/chevron.svg';
import MuiSelect from '../../../../components/widgets/MuiSelect';
import TextField from '../../../../components/molecules/TextField';
import Tag from '../../../../components/atoms/Tag';
import Button from '../../../../components/atoms/Button';
import Tooltip from '../../../../components/molecules/Tooltip';
import Icon, { IconSizes, IconVariants } from '../../../../components/foundations/Icon';
import { AutoCompleteOptionType } from '../../../../components/molecules/autocompleteTypes';
import {
    countActiveConditions,
    createEmptyCondition,
    formatMetricFilterValue,
    getOperatorSymbol,
    isConditionComplete,
    METRIC_FILTER_OPERATOR_OPTIONS,
    MetricFilterOperators,
    normalizeConditionBounds,
} from '../../../metricFilterUtils';

const KEY_PREFIX = 'STATIC.PAGES.CREATIVE_GALLERY.METRIC_FILTER';
export const METRIC_FILTER_LABEL = `${KEY_PREFIX}.LABEL`;
export const TRIGGER_PLACEHOLDER = `${KEY_PREFIX}.TRIGGER_PLACEHOLDER`;
export const POPOVER_TITLE = `${KEY_PREFIX}.POPOVER_TITLE`;
export const POPOVER_SUBTITLE = `${KEY_PREFIX}.POPOVER_SUBTITLE`;
export const EMPTY_HINT = `${KEY_PREFIX}.EMPTY_HINT`;
export const ADD_CONDITION = `${KEY_PREFIX}.ADD_CONDITION`;
export const CLEAR_ALL = `${KEY_PREFIX}.CLEAR_ALL`;
export const APPLY = `${KEY_PREFIX}.APPLY`;
export const REMOVE_CONDITION = `${KEY_PREFIX}.REMOVE_CONDITION`;
export const VALUE_PLACEHOLDER = `${KEY_PREFIX}.VALUE_PLACEHOLDER`;
export const MIN_PLACEHOLDER = `${KEY_PREFIX}.MIN_PLACEHOLDER`;
export const MAX_PLACEHOLDER = `${KEY_PREFIX}.MAX_PLACEHOLDER`;

export const METRIC_FILTER_TRIGGER_TEST_ID = 'metricFilterTrigger';
export const ADD_CONDITION_TEST_ID = 'metricFilterAddCondition';
export const CLEAR_ALL_TEST_ID = 'metricFilterClearAll';
export const conditionRowTestId = id => `metricFilterCondition-${id}`;
export const removeConditionTestId = id => `metricFilterRemoveCondition-${id}`;
export const removeTagTestId = id => `metricFilterRemoveTag-${id}`;
export const MORE_TAGS_TEST_ID = 'metricFilterMoreTag';

const TRIGGER_TAGS_LIMIT = 1;

export const METRIC_FILTER_EDITED_EVENT = 'Metrics Filter';
export const MetricFilterEditActions = {
    ADDED: 'added',
    REMOVED: 'removed',
};

const NON_POSITIVE_FLOAT_KEYS = ['e', 'E', '+', '-'];
const blockNonPositiveFloatKey = event => {
    if (NON_POSITIVE_FLOAT_KEYS.includes(event.key)) {
        event.preventDefault();
    }
};

const MetricFilter = ({
    availableFields = [],
    conditions = [],
    onConditionsChange,
    disabled = false,
    trackEvent,
    translate,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [draftConditions, setDraftConditions] = useState(conditions);

    const appliedConditionsRef = useRef(conditions);

    useEffect(() => {
        if (anchorEl && conditions !== appliedConditionsRef.current) {
            setDraftConditions(conditions);
        }
        appliedConditionsRef.current = conditions;
    }, [conditions, anchorEl]);

    const operatorOptions = useMemo(
        () => METRIC_FILTER_OPERATOR_OPTIONS.map(({ value, label }) => ({ value, label: translate(label) })),
        [translate]
    );

    const appliedCount = countActiveConditions(conditions);
    const hasFields = availableFields.length > 0;

    const trackEdited = (action, params) => trackEvent(METRIC_FILTER_EDITED_EVENT, { event: action, ...params });

    const hasIncompleteCondition = draftConditions.some(condition => !isConditionComplete(condition));
    const addConditionDisabled = disabled || !hasFields || hasIncompleteCondition;

    const completeConditions = conditions.filter(isConditionComplete);
    const visibleConditions = completeConditions.slice(0, TRIGGER_TAGS_LIMIT);
    const hiddenCount = completeConditions.length - visibleConditions.length;

    const isSameCondition = (a, b) =>
        a.field === b.field &&
        a.operator === b.operator &&
        String(a.value ?? '') === String(b.value ?? '') &&
        String(a.min ?? '') === String(b.min ?? '') &&
        String(a.max ?? '') === String(b.max ?? '');

    const trackCommittedEdits = committedConditions => {
        const previousById = new Map(conditions.map(condition => [condition.id, condition]));
        committedConditions.forEach(condition => {
            const previous = previousById.get(condition.id);
            if (!previous || !isSameCondition(previous, condition)) {
                trackEdited(MetricFilterEditActions.ADDED, { metric: condition.field, operator: condition.operator });
            }
        });
        const committedIds = new Set(committedConditions.map(({ id }) => id));
        conditions
            .filter(({ id }) => !committedIds.has(id))
            .forEach(condition =>
                trackEdited(MetricFilterEditActions.REMOVED, { metric: condition.field, operator: condition.operator })
            );
    };

    const commitAndClose = () => {
        setAnchorEl(null);
        const committedConditions = draftConditions.filter(isConditionComplete).map(normalizeConditionBounds);
        trackCommittedEdits(committedConditions);
        startTransition(() => {
            onConditionsChange(committedConditions);
        });
    };

    const togglePopover = event => {
        if (disabled) return;
        if (anchorEl) {
            commitAndClose();
        } else {
            setDraftConditions(conditions);
            setAnchorEl(event.currentTarget);
        }
    };

    const addCondition = () => {
        const nextId = Math.max(0, ...conditions.map(({ id }) => id), ...draftConditions.map(({ id }) => id)) + 1;
        const condition = createEmptyCondition(nextId, availableFields[0]?.value || '');
        setDraftConditions([...draftConditions, condition]);
    };

    const updateCondition = (id, patch) => {
        setDraftConditions(
            draftConditions.map(condition => (condition.id === id ? { ...condition, ...patch } : condition))
        );
    };

    const removeCondition = id => {
        setDraftConditions(draftConditions.filter(condition => condition.id !== id));
    };

    const removeAppliedCondition = id => {
        const removed = conditions.find(condition => condition.id === id);
        onConditionsChange(conditions.filter(condition => condition.id !== id));
        trackEdited(MetricFilterEditActions.REMOVED, { metric: removed?.field, operator: removed?.operator });
    };

    const clearAll = () => {
        setDraftConditions([]);
    };

    const renderConditionTag = condition => {
        const fieldOption = availableFields.find(({ value }) => value === condition.field);
        const label = fieldOption?.label ?? condition.field;
        const displayLabel = fieldOption?.shortLabel || label;
        const isPercentage = !!fieldOption?.isPercentage;
        const removeIcon = (
            <Icon
                name="x"
                size={IconSizes.X_SMALL}
                data-testid={removeTagTestId(condition.id)}
                onClick={event => {
                    event.stopPropagation();
                    removeAppliedCondition(condition.id);
                }}
            />
        );

        const operator = getOperatorSymbol(condition.operator);
        const isBetween = condition.operator === MetricFilterOperators.BETWEEN;
        const min = formatMetricFilterValue(condition.min, isPercentage);
        const max = formatMetricFilterValue(condition.max, isPercentage);
        const formattedValue = formatMetricFilterValue(condition.value, isPercentage);

        const operatorSign = symbol => <span className={css.tagOperator}>{symbol}</span>;

        const conditionContent = lbl =>
            isBetween ? (
                <>
                    {min} {operatorSign('≤')} {lbl} {operatorSign('≤')} {max}
                </>
            ) : (
                <>
                    {lbl} {operatorSign(operator)} {formattedValue}
                </>
            );

        const tag = (
            <Tag
                className={css.tag}
                color="var(--blue100)"
                isFadingBackground={false}
                hidePreview
                value={conditionContent(displayLabel)}
                rightIcon={removeIcon}
            />
        );

        return (
            <Tooltip key={condition.id} title={conditionContent(label)} wrapperClass={css.tagTooltip}>
                {tag}
            </Tooltip>
        );
    };

    const renderConditionRow = condition => {
        const fieldOption = availableFields.find(({ value }) => value === condition.field);
        const isPercentage = !!fieldOption?.isPercentage;

        const renderValueInput = (placeholder, value, onChange) => (
            <div className={css.valueInputWrap}>
                <TextField
                    type="number"
                    inputConfig={{ min: 0 }}
                    containerClass={css.valueInput}
                    value={value}
                    placeholder={placeholder}
                    onChange={onChange}
                    onKeyDown={blockNonPositiveFloatKey}
                    disabled={disabled}
                />
                {isPercentage && <span className={css.unitSuffix}>%</span>}
            </div>
        );

        return (
            <div key={condition.id} className={css.conditionRow} data-testid={conditionRowTestId(condition.id)}>
                <div className={css.fieldSelect}>
                    <MuiSelect
                        value={condition.field}
                        options={availableFields}
                        onChange={value => updateCondition(condition.id, { field: value })}
                        disabled={disabled}
                    />
                </div>
                <div className={css.operatorSelect}>
                    <MuiSelect
                        value={condition.operator}
                        options={operatorOptions}
                        onChange={value => updateCondition(condition.id, { operator: value })}
                        disabled={disabled}
                    />
                </div>
                {condition.operator === MetricFilterOperators.BETWEEN ? (
                    <div className={css.betweenInputs}>
                        {renderValueInput(MIN_PLACEHOLDER, condition.min, value =>
                            updateCondition(condition.id, { min: value })
                        )}
                        {renderValueInput(MAX_PLACEHOLDER, condition.max, value =>
                            updateCondition(condition.id, { max: value })
                        )}
                    </div>
                ) : (
                    renderValueInput(VALUE_PLACEHOLDER, condition.value, value =>
                        updateCondition(condition.id, { value })
                    )
                )}
                <Icon
                    name="x"
                    size={IconSizes.SMALL}
                    className={css.removeIcon}
                    hoverText={REMOVE_CONDITION}
                    data-testid={removeConditionTestId(condition.id)}
                    onClick={() => removeCondition(condition.id)}
                />
            </div>
        );
    };

    return (
        <div className={css.container}>
            <span className={css.label}>
                <Icon name="chart-bar" size={IconSizes.MEDIUM} interactive={false} disabled={disabled} />
                <Translate id={METRIC_FILTER_LABEL} />
            </span>
            <div
                className={classNames(css.trigger, { [css.disabled]: disabled, [css.open]: Boolean(anchorEl) })}
                data-testid={METRIC_FILTER_TRIGGER_TEST_ID}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={togglePopover}
                onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        togglePopover(event);
                    }
                }}
            >
                <div className={css.triggerLabel}>
                    {appliedCount ? (
                        <div className={css.triggerTags}>
                            {visibleConditions.map(renderConditionTag)}
                            {hiddenCount > 0 && (
                                <span className={css.moreTag} data-testid={MORE_TAGS_TEST_ID}>
                                    {`+${hiddenCount}`}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className={css.placeholder}>
                            <Translate id={TRIGGER_PLACEHOLDER} />
                        </span>
                    )}
                </div>
                <div className={css.triggerRight}>
                    <Chevron className={classNames(css.chevron, { [css.chevronOpen]: Boolean(anchorEl) })} />
                </div>
            </div>

            <MuiPopover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={commitAndClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: css.popoverPaper }}
            >
                <div className={css.popoverHeader}>
                    <span className={css.popoverTitle}>
                        <Translate id={POPOVER_TITLE} />
                    </span>
                    <span className={css.popoverSubtitle}>
                        <Translate id={POPOVER_SUBTITLE} />
                    </span>
                </div>
                <div className={css.conditions}>
                    {draftConditions.length ? (
                        draftConditions.map(renderConditionRow)
                    ) : (
                        <div className={css.emptyHint}>
                            <Translate id={EMPTY_HINT} />
                        </div>
                    )}
                </div>
                <span
                    className={classNames(css.addCondition, { [css.disabled]: addConditionDisabled })}
                    data-testid={ADD_CONDITION_TEST_ID}
                    onClick={addConditionDisabled ? undefined : addCondition}
                >
                    <Icon name="plus" size={IconSizes.SMALL} interactive={false} variant={IconVariants.ACTIVE} />
                    <Translate id={ADD_CONDITION} />
                </span>
                {(draftConditions.length > 0 || appliedCount > 0) && (
                    <div className={css.footer}>
                        {draftConditions.length > 0 && (
                            <span
                                className={css.clearAll}
                                data-testid={CLEAR_ALL_TEST_ID}
                                onClick={disabled ? undefined : clearAll}
                            >
                                <Translate id={CLEAR_ALL} />
                            </span>
                        )}
                        <Button
                            className={css.applyButton}
                            text={APPLY}
                            type="primary"
                            level="level2"
                            disabled={disabled}
                            onClick={commitAndClose}
                        />
                    </div>
                )}
            </MuiPopover>
        </div>
    );
};

MetricFilter.propTypes = {
    availableFields: PropTypes.arrayOf(AutoCompleteOptionType),
    conditions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            field: PropTypes.string,
            operator: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ),
    onConditionsChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    trackEvent: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(MetricFilter);
